import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  IconCalendarTime,
  IconCloudDownload,
  IconLibraryPhoto,
  IconMusic,
  IconToolsKitchen2,
  IconUpload,
} from "@tabler/icons-react";
import { useSite } from "../../../store";
import { hasTimelineEvents } from "../../../utils/timeline";
import { hasMenuItems } from "../../../utils/menu";

export interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  end?: boolean;
}

/**
 * The wedding's navigable destinations, derived from its enabled features.
 * Upload is always present; Gallery and Download appear only when permitted.
 * Shared between the main navigation and the action bar so both agree on how
 * many destinations exist (the action bar offsets itself above the mobile tab
 * bar, which only renders when there are at least two destinations).
 */
export const useNavItems = (): NavItem[] => {
  const { t } = useTranslation();
  const { allowGallery, allowZipDownload, allowTimeline, allowMenu, allowDjRequests, config } = useSite();

  const items: NavItem[] = [
    { to: "/", label: t("menu.upload"), icon: <IconUpload />, end: true },
  ];
  if (allowGallery) {
    items.push({
      to: "/gallery",
      label: t("menu.gallery"),
      icon: <IconLibraryPhoto />,
    });
  }
  if (allowZipDownload) {
    items.push({
      to: "/download",
      label: t("menu.download"),
      icon: <IconCloudDownload />,
    });
  }
  if (allowTimeline && hasTimelineEvents(config.timeline)) {
    items.push({
      to: "/timeline",
      label: t("menu.timeline"),
      icon: <IconCalendarTime />,
    });
  }
  if (allowMenu && hasMenuItems(config.menu)) {
    items.push({
      to: "/menu",
      label: t("menu.foodMenu"),
      icon: <IconToolsKitchen2 />,
    });
  }
  if (allowDjRequests) {
    items.push({
      to: "/dj-requests",
      label: t("menu.djRequests"),
      icon: <IconMusic />,
    });
  }
  return items;
};
